import { tassign } from 'tassign';
import values from 'lodash/values';

import { ShoppingCartActionsV2 } from './shopping-cart-v2.actions';
import { ShoppingCartAction, ShoppingCartActions } from './shopping-cart.actions';
import { FailedCartTicket } from '@ridango/core';

export interface ShoppingCartState {
    id: number;
    created_at: string;
    updated_at: string;
    user_id: number;
    shopping_cart_id: number;
    total: number;
    status: string;
    inWebPaymentSelected: boolean;
    org_id: number;
    timestamp: string;
    a_date: string;
    type: string;
    customer_id: number;
    total_sum: number;
    currency: string;
    external_code: string;
    region_id: number;
    docNums: any[];
    ticketsByDocNum: any;
    tickets: any;
    ticketsGrouped: any;
    ticketsGroupedUids: number[];
    voucherTicket: any;
    countdownTimer: number;
    createdTimestamp: number;
    jwt_token: string;
    termsAgreed: boolean;
    failedTickets: FailedCartTicket[];

    ticketPayloadsPosted: boolean;
}

export const SHOPPING_CART_INITIAL_STATE: ShoppingCartState = {
    id: null,
    created_at: null,
    updated_at: null,
    user_id: null,
    shopping_cart_id: null,
    total: null,
    status: null,
    inWebPaymentSelected: false,
    org_id: null,
    timestamp: null,
    a_date: null,
    type: null,
    customer_id: null,
    total_sum: null,
    currency: null,
    external_code: null,
    region_id: null,
    docNums: [],
    ticketsByDocNum: {},
    tickets: {},
    ticketsGrouped: [],
    ticketsGroupedUids: [],
    voucherTicket: null,
    // 15 minutes as a default
    countdownTimer: 900,
    createdTimestamp: 0,
    jwt_token: null,
    termsAgreed: false,
    failedTickets: [],
    ticketPayloadsPosted: false
};

export const SHOPPING_CART_STATE_KEY = 'cart';

export function shoppingCartReducer(
    lastState: ShoppingCartState = SHOPPING_CART_INITIAL_STATE,
    action: ShoppingCartAction
): ShoppingCartState {
    // NB read this: https://redux.js.org/usage/structuring-reducers/updating-normalized-data
    switch (action.type) {
        case ShoppingCartActions.UPDATE_SHOPPING_CART: {
            const payload = { ...action.payload, ticketPayloadsPosted: false };
            const incomingTicketsNormalized: Record<string, any> = {};

            if (payload.tickets) {
                const ticketsArray = Array.isArray(payload.tickets) ? payload.tickets : values(payload.tickets);

                ticketsArray.forEach((ticket: any) => {
                    const existingTicket =
                        lastState.tickets?.[ticket.doc_num] || lastState.ticketsByDocNum?.[ticket.doc_num];
                    incomingTicketsNormalized[ticket.doc_num] = existingTicket
                        ? { ...existingTicket, ...ticket }
                        : ticket;
                });
            }

            const mergedTickets = {
                ...(lastState.tickets ?? {}),
                ...incomingTicketsNormalized
            };

            if (!lastState.shopping_cart_id) {
                payload.createdTimestamp = Date.now();
            }

            return tassign(lastState, {
                ...payload,
                tickets: mergedTickets,
                ticketsByDocNum: mergedTickets
            });
        }
        case ShoppingCartActions.UPDATE_CORP_SHOPPING_CART:
            if (lastState.tickets && action.payload.tickets) {
                action.payload.docNums = Object.keys(action.payload.tickets).map((k) => +k);
                action.payload.tickets = Object.assign(lastState.tickets, action.payload.tickets);
            }
            if (!lastState.shopping_cart_id) {
                action.payload.createdTimestamp = new Date().getTime();
            }
            return tassign(lastState, action.payload);
        case ShoppingCartActions.ADD_TICKET_TO_CART:
            return tassign(lastState, {});
        case ShoppingCartActions.ADD_VOUCHER_TICKET_TO_CART:
            return tassign(lastState, {
                voucherTicket: action.payload
            });
        case ShoppingCartActions.DELETE_SHOPPING_CART:
            return {
                ...lastState,
                ...SHOPPING_CART_INITIAL_STATE,
                tickets: {},
                ticketsByDocNum: {}
            };
        case ShoppingCartActions.REMOVE_TICKET_FROM_CART:
            return tassign(lastState, {
                docNums: [
                    ...lastState.docNums.slice(0, lastState.docNums.indexOf(action.payload.ticket.doc_num)),
                    ...lastState.docNums.slice(lastState.docNums.indexOf(action.payload.ticket.doc_num) + 1)
                ]
            });
        case ShoppingCartActions.REMOVE_GROUPED_TICKET_FROM_CART: {
            const ticketDocNums = [
                ...lastState.ticketsGrouped[action.payload.uid].docNums.slice(
                    0,
                    lastState.ticketsGrouped[action.payload.uid].docNums.indexOf(action.payload.ticket.doc_num)
                ),
                ...lastState.ticketsGrouped[action.payload.uid].docNums.slice(
                    lastState.ticketsGrouped[action.payload.uid].docNums.indexOf(action.payload.ticket.doc_num) + 1
                )
            ];
            // let price = 0;

            return tassign(lastState, {
                docNums: [
                    ...lastState.docNums.slice(0, lastState.docNums.indexOf(action.payload.ticket.doc_num)),
                    ...lastState.docNums.slice(lastState.docNums.indexOf(action.payload.ticket.doc_num) + 1)
                ],
                ticketsGrouped: {
                    ...lastState.ticketsGrouped,
                    [action.payload.uid]: {
                        ...lastState.ticketsGrouped[action.payload.uid],
                        docNums: ticketDocNums,
                        price: ticketDocNums.reduce((price, docNum) => price + lastState.tickets[docNum].price, 0)
                    }
                }
            });
        }
        case ShoppingCartActions.UPDATE_CART_TOTAL_PRICE:
            return tassign(lastState, {
                total_sum: action.payload.total_sum,
                total: action.payload.total
            });
        case ShoppingCartActions.ADD_LOCAL_TICKET_TO_CART:
            return tassign(lastState, {
                // docNums: [ ...lastState.docNums, action.payload.serverTicket.doc_num + '' ],
                docNums: [...lastState.docNums, action.payload.serverTicket.doc_num],
                ticketsGrouped: {
                    ...lastState.ticketsGrouped,
                    [action.payload.localTicket.uid]: {
                        ...action.payload.localTicket,
                        docNums: [
                            ...lastState.ticketsGrouped[action.payload.localTicket.uid].docNums,
                            action.payload.serverTicket.doc_num
                        ]
                    }
                }
            });
        case ShoppingCartActions.ADD_LOCAL_TICKET_UID_TO_CART:
            return tassign(lastState, {
                ticketsGrouped: { ...lastState.ticketsGrouped, [action.payload.ticket.uid]: { docNums: [] } },
                ticketsGroupedUids: [...lastState.ticketsGroupedUids, action.payload.ticket.uid]
            });
        case ShoppingCartActions.DECREMENT_CART_TIMER:
            return tassign(lastState, {
                countdownTimer: lastState.countdownTimer - 1
            });
        case ShoppingCartActions.SET_CART_TIMER:
            return tassign(lastState, {
                countdownTimer: action.payload
            });
        case ShoppingCartActions.SET_CART_STATUS:
            return tassign(lastState, {
                status: action.payload
            });
        case ShoppingCartActions.UPDATE_LOCAL_TICKET_PRICE:
            return tassign(lastState, {
                ticketsGrouped: {
                    ...lastState.ticketsGrouped,
                    [action.payload.uid]: {
                        ...lastState.ticketsGrouped[action.payload.uid],
                        price: action.payload.price
                    }
                }
            });
        case ShoppingCartActions.CLEAR_CART:
            return tassign(lastState, SHOPPING_CART_INITIAL_STATE);
        case ShoppingCartActions.SET_CART_TERMS_AGREED:
            return tassign(lastState, { termsAgreed: action.payload.termsAgreed });
        case ShoppingCartActions.HYDRATE_CART_STATE:
            return tassign(lastState, action.payload);
        case ShoppingCartActionsV2.POST_TICKET_PAYLOADS:
            return tassign(lastState, { ticketPayloadsPosted: false });
        case ShoppingCartActionsV2.POST_TICKET_PAYLOADS_SUCCESS:
            if (lastState.createdTimestamp === 0) {
                action.payload.createdTimestamp = new Date().getTime();
            } else {
                action.payload.createdTimestamp = lastState.createdTimestamp;
            }

            return tassign(lastState, {
                shopping_cart_id: action.payload.shopping_cart_id,
                status: action.payload.status,
                ticketsByDocNum: { ...lastState.ticketsByDocNum, ...action.payload.tickets },
                docNums: [...lastState.docNums, ...action.payload.docNums],
                createdTimestamp: action.payload.createdTimestamp,
                ticketPayloadsPosted: true
            });
        case ShoppingCartActionsV2.POST_TICKET_PAYLOADS_FAILED:
            return tassign(lastState, { failedTickets: action.payload });
        case ShoppingCartActionsV2.SET_IN_WEB_PAYMENT_SELECTED:
            return tassign(lastState, { inWebPaymentSelected: action.payload.inWebPaymentSelected });
    }

    return lastState;
}

export interface ShoppingCartPartialStoreState {
    [SHOPPING_CART_STATE_KEY]: ShoppingCartState;
}
